/**
 * Hausje controlled-release measurement.
 *
 * Destination: Shopify Customer Events (`Shopify.analytics.publish`), a
 * short first-party log in localStorage (`hausje:measure:v1`), and a
 * consent-gated sendBeacon to Hausje's Cloudflare measurement Worker.
 * Hausje has no Google Analytics, Meta, or TikTok pixel.
 *
 * Every event is analytics, not essential. Nothing is written or published
 * until Shopify's Customer Privacy API reports
 * `analyticsProcessingAllowed() === true`.
 */

const CENTRAL_ENDPOINT = 'https://hausje-measure.hausje.workers.dev/events';

const LOG_KEY = 'hausje:measure:v1';
const SEEN_KEY = 'hausje:impressions:v1';
const RETURN_KEY = 'hausje:return-marker:v1';
const SESSION_KEY = 'hausje:session-open:v1';
/** Last valid campaign set for this browser tab/session only (sessionStorage). */
export const ATTRIBUTION_KEY = 'hausje:campaign:v1';
const LOG_LIMIT = 80;
const SEEN_LIMIT = 400;
export const RETURN_GAP_MS = 4 * 60 * 60 * 1000;

export const UTM_FIELD_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];

const CARD_SELECTOR = '[data-hausje-discovery-card], product-card[data-product-id]';

const EVENT = {
  impression: 'hausje_product_impression',
  click: 'hausje_product_click',
  save: 'hausje_save',
  unsave: 'hausje_unsave',
  retailer: 'hausje_retailer_click',
  returnVisit: 'hausje_return_visit',
};

/**
 * @param {object | null | undefined} privacy
 * @returns {boolean}
 */
export function consentAllowsAnalytics(privacy) {
  if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
  try {
    return privacy.analyticsProcessingAllowed() === true;
  } catch {
    return false;
  }
}

/**
 * @param {{ now: number, lastSeen: number | null, sessionOpen: boolean }} state
 */
export function returnVisitDecision(state) {
  const now = state.now;
  if (state.sessionOpen) {
    return { fire: false, nextLastSeen: state.lastSeen, openSession: true };
  }
  if (state.lastSeen == null) {
    return { fire: false, nextLastSeen: now, openSession: true };
  }
  if (now - state.lastSeen >= RETURN_GAP_MS) {
    return { fire: true, nextLastSeen: now, openSession: true };
  }
  return { fire: false, nextLastSeen: now, openSession: true };
}

/**
 * @param {string} handle
 * @param {string} surface
 * @param {Set<string>} seen
 * @returns {boolean} true when this pair should be recorded
 */
export function claimImpression(handle, surface, seen) {
  const key = `${surface}|${handle}`;
  if (!handle || !surface || seen.has(key)) return false;
  seen.add(key);
  return true;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function cleanUtmToken(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const text = String(value).trim();
  if (!text || text.length > 80) return '';
  if (/https?:/i.test(text) || text.includes('://') || text.includes('@')) return '';
  if (/[/?&=]/.test(text)) return '';
  return text;
}

/**
 * Parse a whole campaign set from a query string. Returns null when no valid UTM.
 * Present fields only — callers must replace stored sets as a whole, never merge.
 * @param {string | URLSearchParams} search
 * @returns {Record<string, string> | null}
 */
export function parseCampaignFromSearch(search) {
  let params;
  try {
    if (search instanceof URLSearchParams) params = search;
    else params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  } catch {
    return null;
  }
  /** @type {Record<string, string>} */
  const set = {};
  let any = false;
  for (const key of UTM_FIELD_KEYS) {
    if (!params.has(key)) continue;
    const cleaned = cleanUtmToken(params.get(key));
    if (!cleaned) continue;
    set[key] = cleaned;
    any = true;
  }
  return any ? set : null;
}

/**
 * @param {Storage | null | undefined} storage
 * @returns {Record<string, string> | null}
 */
export function readStoredCampaign(storage) {
  if (!storage || typeof storage.getItem !== 'function') return null;
  try {
    const raw = storage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    /** @type {Record<string, string>} */
    const set = {};
    let any = false;
    for (const key of UTM_FIELD_KEYS) {
      const cleaned = cleanUtmToken(parsed[key]);
      if (!cleaned) continue;
      set[key] = cleaned;
      any = true;
    }
    return any ? set : null;
  } catch {
    return null;
  }
}

/**
 * @param {Storage | null | undefined} storage
 * @param {Record<string, string> | null} set
 */
export function writeStoredCampaign(storage, set) {
  if (!storage || typeof storage.setItem !== 'function') return;
  try {
    if (!set) {
      storage.removeItem(ATTRIBUTION_KEY);
      return;
    }
    /** @type {Record<string, string>} */
    const clean = {};
    let any = false;
    for (const key of UTM_FIELD_KEYS) {
      const cleaned = cleanUtmToken(set[key]);
      if (!cleaned) continue;
      clean[key] = cleaned;
      any = true;
    }
    if (!any) {
      storage.removeItem(ATTRIBUTION_KEY);
      return;
    }
    storage.setItem(ATTRIBUTION_KEY, JSON.stringify(clean));
  } catch {
    /* private mode / quota — never block wishlist or retailer */
  }
}

/**
 * Clear session campaign attribution (consent denied/withdrawn or new empty session).
 * @param {Storage | null | undefined} storage
 */
export function clearStoredCampaign(storage) {
  writeStoredCampaign(storage, null);
}

/**
 * Session rule: last valid campaign in this browser session.
 * - New valid UTM set in the URL replaces the whole stored set (no field merge).
 * - Internal navigation without UTM keeps the stored set.
 * - Without consent: never store or return campaign; clear any prior store.
 * - Consent granted after navigation: only the current URL can seed; earlier UTMs are not recovered.
 *
 * @param {{ search?: string | URLSearchParams, storage?: Storage | null, consent: boolean }} opts
 * @returns {Record<string, string> | null}
 */
export function syncCampaignAttribution(opts) {
  const storage = opts.storage;
  if (!opts.consent) {
    clearStoredCampaign(storage);
    return null;
  }
  const fromUrl = parseCampaignFromSearch(opts.search || '');
  if (fromUrl) {
    writeStoredCampaign(storage, fromUrl);
    return fromUrl;
  }
  return readStoredCampaign(storage);
}

/**
 * Keep only the measurement fields. Drop URLs and anything else.
 * @param {Record<string, unknown>} input
 */
export function minimalPayload(input) {
  const out = {};
  const handle = cleanToken(input.product_handle);
  const id = cleanToken(input.product_id);
  const retailer = cleanRetailer(input.retailer);
  const surface = cleanToken(input.surface);
  const placement = cleanToken(input.placement);
  const ranking = cleanToken(input.ranking_version);
  if (handle) out.product_handle = handle;
  if (id) out.product_id = id;
  if (retailer) out.retailer = retailer;
  if (surface) out.surface = surface;
  if (Number.isInteger(input.position) && input.position > 0) out.position = input.position;
  if (ranking) out.ranking_version = ranking;
  if (placement) out.placement = placement;
  for (const key of UTM_FIELD_KEYS) {
    const cleaned = cleanUtmToken(input[key]);
    if (cleaned) out[key] = cleaned;
  }
  return out;
}

/**
 * @param {unknown} value
 */
function cleanToken(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const text = String(value).trim();
  if (!text || /^https?:/i.test(text) || text.includes('://')) return '';
  return text.slice(0, 120);
}

/**
 * @param {unknown} value
 */
function cleanRetailer(value) {
  const text = cleanToken(value);
  if (!text || /[/?&=]/.test(text)) return '';
  return text;
}

/**
 * Attach the current session campaign (if any) onto a payload copy.
 * Never throws; never reads storage without consent.
 * @param {Record<string, unknown>} payload
 * @param {{ storage?: Storage | null, search?: string | URLSearchParams, consent: boolean }} opts
 */
export function withCampaignAttribution(payload, opts) {
  const base = payload && typeof payload === 'object' ? { ...payload } : {};
  try {
    const campaign = syncCampaignAttribution(opts);
    if (!campaign) return base;
    return { ...base, ...campaign };
  } catch {
    return base;
  }
}

/**
 * @param {string} name
 * @param {Record<string, unknown>} payload
 */
export function buildCentralRecord(name, payload) {
  return { event_name: name, ...minimalPayload(payload) };
}

/**
 * Non-blocking central delivery. Beacon first, then keepalive fetch.
 * @param {string} url
 * @param {Record<string, unknown>} record
 * @param {{ sendBeacon?: (url: string, body: Blob) => boolean, fetch?: (url: string, init: RequestInit) => Promise<unknown> } | null | undefined} transport
 */
export function sendCentral(url, record, transport) {
  if (!url || !record || !record.event_name) return false;
  let json;
  try {
    json = JSON.stringify(record);
  } catch {
    return false;
  }
  try {
    if (transport && typeof transport.sendBeacon === 'function') {
      const blob = new Blob([json], { type: 'text/plain;charset=UTF-8' });
      if (transport.sendBeacon(url, blob) === true) return true;
    }
  } catch {
    /* fall through to fetch */
  }
  try {
    if (transport && typeof transport.fetch === 'function') {
      const pending = transport.fetch(url, {
        method: 'POST',
        body: json,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        keepalive: true,
        mode: 'cors',
        credentials: 'omit',
      });
      if (pending && typeof pending.catch === 'function') pending.catch(() => {});
      return true;
    }
  } catch {
    /* analytics must not block the page */
  }
  return false;
}

/**
 * @param {string} name
 * @param {Record<string, unknown>} payload
 * @param {{ publish?: (name: string, payload: Record<string, unknown>) => void, forward?: (name: string, payload: Record<string, unknown>) => void, storage?: Storage, attribute?: (payload: Record<string, unknown>) => Record<string, unknown> }} sinks
 */
export function deliverEvent(name, payload, sinks) {
  let attributed = payload;
  try {
    if (sinks && typeof sinks.attribute === 'function') attributed = sinks.attribute(payload) || payload;
  } catch {
    attributed = payload;
  }
  const body = minimalPayload(attributed);
  try {
    sinks.publish?.(name, body);
  } catch {
    /* analytics must not block the page */
  }
  try {
    sinks.forward?.(name, body);
  } catch {
    /* analytics must not block the page */
  }
  try {
    const storage = sinks.storage;
    if (!storage) return body;
    const raw = storage.getItem(LOG_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(list) ? list : [];
    next.push({ name, payload: body });
    while (next.length > LOG_LIMIT) next.shift();
    storage.setItem(LOG_KEY, JSON.stringify(next));
  } catch {
    /* private mode or a full quota — wishlist/retailer must not block */
  }
  return body;
}

function pageSurface() {
  const path = window.location.pathname;
  if (path === '/' || path === '') return 'homepage';
  if (path.includes('/pages/voor-je-hausje')) return 'voor-je-hausje';
  if (path.startsWith('/collections/')) return 'collection';
  if (path.startsWith('/search')) return 'search';
  if (path.startsWith('/products/')) return 'product';
  if (path.includes('/pages/klein-hausje')) return 'klein-hausje';
  return 'other';
}

/**
 * @param {Element} card
 */
function surfaceFor(card) {
  const page = pageSurface();
  const context = card.getAttribute('data-product-view-context') || '';
  if (page === 'homepage' && (context === 'inspiration-exact' || context === 'inspiration-comparable')) {
    return context;
  }
  if (page !== 'other') return page;
  return context || 'discovery';
}

/**
 * @param {Element} card
 */
function handleOf(card) {
  const direct = card.getAttribute('data-product-handle');
  if (direct) return direct.trim();
  const link = card.querySelector('a[href*="/products/"]');
  if (!(link instanceof HTMLAnchorElement)) return '';
  try {
    const url = new URL(link.getAttribute('href') || '', window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    const index = parts.indexOf('products');
    return index >= 0 ? decodeURIComponent(parts[index + 1] || '') : '';
  } catch {
    return '';
  }
}

/**
 * @param {Element} card
 */
function retailerOf(card) {
  const brand = card.querySelector('.hausje-discovery-card__brand, .product-card__vendor, .hausje-product-card__vendor');
  const text = brand ? brand.textContent.replace(/\s+/g, ' ').trim() : '';
  return text;
}

/**
 * @param {Element} card
 * @param {string} surface
 */
function positionOf(card, surface) {
  const cards = [...document.querySelectorAll(CARD_SELECTOR)].filter((node) => surfaceFor(node) === surface);
  const index = cards.indexOf(card);
  return index >= 0 ? index + 1 : null;
}

function productHandleFromPath() {
  const parts = window.location.pathname.split('/').filter(Boolean);
  const index = parts.indexOf('products');
  return index >= 0 ? decodeURIComponent(parts[index + 1] || '') : '';
}

function loadSeen() {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list.filter((item) => typeof item === 'string') : []);
  } catch {
    return new Set();
  }
}

/**
 * @param {Set<string>} seen
 */
function storeSeen(seen) {
  try {
    const list = [...seen];
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(-SEEN_LIMIT)));
  } catch {
    /* ignore */
  }
}

function publishToShopify(name, payload) {
  const analytics = window.Shopify && window.Shopify.analytics;
  if (!analytics || typeof analytics.publish !== 'function') return;
  analytics.publish(name, payload);
}

function sessionAttributionStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function currentCampaignPayload(payload) {
  const privacy = window.Shopify && window.Shopify.customerPrivacy;
  const consent = consentAllowsAnalytics(privacy);
  return withCampaignAttribution(payload, {
    consent,
    storage: sessionAttributionStorage(),
    search: window.location && window.location.search ? window.location.search : '',
  });
}

function forwardCentral(name, payload) {
  const privacy = window.Shopify && window.Shopify.customerPrivacy;
  if (!consentAllowsAnalytics(privacy)) return;
  // payload is already attributed + minimized by deliverEvent when sinks.attribute is set
  const beacon = typeof navigator !== 'undefined' ? navigator.sendBeacon : undefined;
  sendCentral(CENTRAL_ENDPOINT, buildCentralRecord(name, payload), {
    sendBeacon: typeof beacon === 'function' ? beacon.bind(navigator) : undefined,
    fetch: typeof fetch === 'function' ? fetch.bind(globalThis) : undefined,
  });
}

function sinks() {
  return {
    publish: publishToShopify,
    storage: window.localStorage,
    forward: forwardCentral,
    attribute: currentCampaignPayload,
  };
}

/**
 * @param {Set<string>} seen
 */
function bindImpressions(seen) {
  if (typeof IntersectionObserver !== 'function') return;
  const observer = new IntersectionObserver(
    (entries) => {
      let changed = false;
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
        const card = entry.target;
        if (!(card instanceof Element)) continue;
        const handle = handleOf(card);
        const surface = surfaceFor(card);
        if (!claimImpression(handle, surface, seen)) {
          observer.unobserve(card);
          continue;
        }
        changed = true;
        observer.unobserve(card);
        const position = positionOf(card, surface);
        deliverEvent(
          EVENT.impression,
          {
            product_handle: handle,
            product_id: card.getAttribute('data-product-id') || '',
            retailer: retailerOf(card),
            surface,
            position,
            placement: surface,
            ranking_version: surface === 'homepage' ? 'v1b3-editorial-v1-1' : '',
          },
          sinks()
        );
      }
      if (changed) storeSeen(seen);
    },
    { threshold: 0.5 }
  );

  const watch = (root) => {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll(CARD_SELECTOR).forEach((card) => observer.observe(card));
  };

  watch(document);
  document.addEventListener('hausje:discovery-appended', (event) => {
    const root = event instanceof CustomEvent ? event.detail && event.detail.root : null;
    watch(root || document);
  });
}

function bindClicks() {
  document.addEventListener('hausje:product-click', (event) => {
    if (!(event instanceof CustomEvent) || !event.detail) return;
    const detail = event.detail;
    const surface = detail.context || pageSurface();
    deliverEvent(
      EVENT.click,
      {
        product_handle: detail.handle || '',
        product_id: detail.product_id || '',
        surface,
        placement: surface,
        ranking_version: surface === 'homepage' ? 'v1b3-editorial-v1-1' : '',
      },
      sinks()
    );
  });

  window.addEventListener('hausje:saved-changed', (event) => {
    if (!(event instanceof CustomEvent) || !event.detail) return;
    const detail = event.detail;
    const name = detail.action === 'add' ? EVENT.save : detail.action === 'remove' ? EVENT.unsave : '';
    if (!name) return;
    const items = Array.isArray(detail.items) ? detail.items : [];
    const match = items.find((item) => item && item.handle === detail.handle);
    deliverEvent(
      name,
      {
        product_handle: detail.handle || '',
        product_id: match && match.id ? String(match.id) : '',
        surface: pageSurface(),
        placement: 'wishlist',
      },
      sinks()
    );
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('[data-hausje-campaign]');
    if (!(link instanceof Element)) return;
    deliverEvent(
      'hausje_campaign_click',
      {
        surface: 'klein-hausje',
        placement: link.getAttribute('data-hausje-campaign') || '',
      },
      sinks()
    );
  });

  document.addEventListener('hausje:retailer-click', (event) => {
    if (!(event instanceof CustomEvent) || !event.detail) return;
    const detail = event.detail;
    const slot = document.querySelector('[data-hausje-offer-slot][data-product-id]');
    deliverEvent(
      EVENT.retailer,
      {
        product_handle: productHandleFromPath(),
        product_id: slot instanceof Element ? slot.getAttribute('data-product-id') || '' : '',
        retailer: detail.retailer || '',
        surface: 'product',
        placement: detail.placement || 'PDP',
      },
      sinks()
    );
  });
}

function bindReturnVisit() {
  let lastSeen = null;
  try {
    const raw = localStorage.getItem(RETURN_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && Number.isFinite(parsed.lastSeen)) lastSeen = parsed.lastSeen;
  } catch {
    lastSeen = null;
  }
  let sessionOpen = false;
  try {
    sessionOpen = sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    sessionOpen = false;
  }
  const decision = returnVisitDecision({ now: Date.now(), lastSeen, sessionOpen });
  try {
    if (!sessionOpen) sessionStorage.setItem(SESSION_KEY, '1');
    if (decision.nextLastSeen != null) {
      localStorage.setItem(RETURN_KEY, JSON.stringify({ lastSeen: decision.nextLastSeen }));
    }
  } catch {
    /* ignore */
  }
  if (decision.fire) {
    deliverEvent(EVENT.returnVisit, { surface: pageSurface(), placement: 'return' }, sinks());
  }
}

function start() {
  // Seed or refresh campaign from the URL only after analytics consent.
  syncCampaignAttribution({
    consent: true,
    storage: sessionAttributionStorage(),
    search: window.location && window.location.search ? window.location.search : '',
  });
  const seen = loadSeen();
  bindImpressions(seen);
  bindClicks();
  bindReturnVisit();
}

function whenConsented(run) {
  let started = false;
  const applyConsent = (privacy) => {
    const allowed = consentAllowsAnalytics(privacy);
    if (!allowed) {
      clearStoredCampaign(sessionAttributionStorage());
      return;
    }
    if (started) {
      // Consent re-granted or preferences updated: sync current URL only.
      syncCampaignAttribution({
        consent: true,
        storage: sessionAttributionStorage(),
        search: window.location && window.location.search ? window.location.search : '',
      });
      return;
    }
    started = true;
    run();
  };

  const shopify = window.Shopify;
  if (!shopify || typeof shopify.loadFeatures !== 'function') return;
  shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
    applyConsent(shopify.customerPrivacy);
  });
  document.addEventListener('visitorConsentCollected', () => {
    applyConsent(shopify.customerPrivacy);
  });
}

if (typeof document !== 'undefined' && !window.__hausjeMeasureStarted) {
  window.__hausjeMeasureStarted = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => whenConsented(start), { once: true });
  } else {
    whenConsented(start);
  }
}
